import { connectorApiUrl } from '@/config'
import { getCurrentUserIdToken } from '@/services/auth'
import { SharedLinkCreate } from '@/store/types/link'
import { errorIfInvalidResponse } from './errors'
import { arrayzeAndExpand, composedFetch } from './helpers'
import {
  listSharedLinksSchema,
  createSharedLinkSchema,
  ApiListSharedLinks,
  ApiCreateSharedLink,
  captureInvalidSchemaOnSentry,
} from './schemas'

/**
 * It exists within ./helpers, but I think that they belong in route-based modules
 *
 * Get the endpoint URL.
 *
 * @returns The share_links endpoint URL
 */
export function getShareLinksAPIURL(): string {
  return `${connectorApiUrl}/share_links/`
}

/**
 * Get the share links ednpoint URL with a given hash at the end to target individual Shared Links.

 * @param hash a hash from the share_links connector api successful response
 * @returns URL for yet-not-existing file API
 */
function getSharedLinkHashAPIURL(hash: string): string {
  if (!hash || typeof hash !== 'string') throw new Error('Hash is missing or is not a string.')
  const urlWithHash = getShareLinksAPIURL() + hash + '/'
  return urlWithHash
}

/**
 * Get an URL to be hit once someone starts browsing a shared link
 *
 * @param hash the shared link's hash
 * @returns an URL
 */
export function getSharedLinkHarshBrowseAPIURL(hash: string): string {
  return getSharedLinkHashAPIURL(hash) + '/browse/'
}

/**
 * Get user created shared links
 * @returns list of links
 */
export const apiListUserLinks = async (): Promise<ApiListSharedLinks> => {
  const response = await composedFetch('GET', getShareLinksAPIURL(), await getCurrentUserIdToken())
  await errorIfInvalidResponse(response)

  const json = await response.json()
  const list = json.result

  captureInvalidSchemaOnSentry(list, listSharedLinksSchema)
  return list
}

/**
 * A hit onto share_links connector endpoint
 * @param ShareLink object containing setup options
 * @returnsthe connector success response.
 */
export const apiCreateSharedLink = async (
  sharedLinkCreate: SharedLinkCreate,
): Promise<ApiCreateSharedLink> => {
  // We get a full SharedLinkCreate object necessary for our API.
  // Unfortunately, Sets do not get JSON.stringified to arrays, so
  // it needs to be converted before we send it.

  // First, snake_casing
  const {
    driveUuid: drive_uuid,
    expiryTimestamp: expiry_timestamp,
    passwordHash: password_hash,

    permissions,
    paths,
    title,
  } = sharedLinkCreate

  const convertedSharedLinkCreate = {
    permissions,
    drive_uuid,
    expiry_timestamp,
    password_hash,
    title,

    // Second, the API expects paths to be a list of objects with "path" property, so we expand them:
    paths: arrayzeAndExpand(paths, 'path'),
  }

  const response = await composedFetch(
    'POST',
    getShareLinksAPIURL(),
    await getCurrentUserIdToken(),
    convertedSharedLinkCreate,
  )
  await errorIfInvalidResponse(response)
  const json = await response.json()

  captureInvalidSchemaOnSentry(json, createSharedLinkSchema)

  return json
}

export const apiDeleteSharedLink = async (hash: string): Promise<any> => {
  const response = await composedFetch(
    'DELETE',
    getSharedLinkHashAPIURL(hash),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response)

  return true
}

//   ___ ___  _ __  ___ _   _ _ __ ___   ___
//  / __/ _ \| '_ \/ __| | | | '_ ` _ \ / _ \
// | (_| (_) | | | \__ \ |_| | | | | | |  __/
//  \___\___/|_| |_|___/\__,_|_| |_| |_|\___|

/**
 * Helper: get API link for item listing
 * @param hash link hash
 * @param path item path within shared link
 * @returns a link for browsing
 */
const getShareLinkBrowseURL = (hash: string, path: string) =>
  `${getShareLinksAPIURL()}${hash}/browse${path}`

/**
 * Hit API to get item listing
 * @param hash shared link hash
 * @param path particular filesystem position path
 * @returns an item listing
 */
export const apiGetSharedListFromLink = async (hash: string, path: string): Promise<any> => {
  const response = await composedFetch('GET', getShareLinkBrowseURL(hash, path))
  await errorIfInvalidResponse(response)
  return await response.json()
}

/** Helper: format URL needed for request for downloadable link
 * @param hash link hash
 * @param itemPath the path of item
 * @returns a fortmatterd URL
 */
const getShareLinkItemDownloadURL = (hash: string, itemPath: string) =>
  `${getShareLinksAPIURL()}${hash}/download?path=${itemPath}`

/**
 * Get a downloadable link
 * @param hash of a shared link
 * @param itemPath
 * @returns
 */
export const apiGetSharedLinkItemDownloadableLink = async (
  hash: string,
  itemPath: string,
): Promise<any> => {
  const fullPath = getShareLinkItemDownloadURL(hash, itemPath)
  const response = await composedFetch('GET', fullPath)
  await errorIfInvalidResponse(response)

  const json = await response.json()
  return json.url
}
